import { record, getRecordConsolePlugin } from 'rrweb';
import type { eventWithTime } from '@rrweb/types';
import { useAuthStore } from '@/stores/authStore';
import { sessionReplayApi } from '@/api/endpoints/sessionReplay';
import { ACCESS_TOKEN_KEY } from '@/lib/constants';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const FLUSH_INTERVAL_MS = 30_000; // 30 seconds
const BUFFER_FLUSH_THRESHOLD = 50; // events
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const IDLE_THROTTLE_MS = 1_000; // throttle idle-reset checks to 1/sec

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let stopRecordingFn: (() => void) | null = null;
let sessionId: string | null = null;
let endSessionToken: string | null = null;
let eventBuffer: eventWithTime[] = [];
let sequenceNumber = 0;
let flushIntervalId: ReturnType<typeof setInterval> | null = null;
let idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
let lastActivityTs = 0;
let isIdle = false;
let isRecording = false;
let isStarting = false;
let closeoutSent = false;
let pendingIdleCallback: number | null = null;

// Cached auth token — survives logout so final flush/end can authenticate
let lastKnownToken: string | null = null;

// Handlers we need to remove later
let handleBeforeUnload: (() => void) | null = null;
let handlePageHide: (() => void) | null = null;
let handleIdleActivity: (() => void) | null = null;

// ---------------------------------------------------------------------------
// rrweb privacy / sampling configuration
// ---------------------------------------------------------------------------

const RRWEB_OPTIONS = {
  maskAllInputs: true,
  maskInputOptions: {
    password: true,
    email: true,
    text: true,
    tel: true,
    textarea: true,
    select: true,
  },
  maskTextSelector: '[data-mask]',
  maskInputFn: (text: string) => '*'.repeat(text.length),
  sampling: {
    mousemove: true,
    mouseInteraction: true,
    scroll: 150,
    media: 800,
    input: 'last' as const,
  },
  slimDOMOptions: {
    script: true,
    comment: true,
    headFavicon: true,
    headWhitespace: true,
    headMetaSocial: true,
    headMetaRobots: true,
    headMetaHttpEquiv: true,
    headMetaAuthorship: true,
    headMetaVerification: true,
  },
  blockClass: 'rrweb-block',
  // Replays run in rrweb's iframe and need the captured CSS that styled the
  // original DOM, especially for Tailwind-rendered CMS/rich content.
  inlineStylesheet: true,
  collectFonts: true,
  recordCanvas: false,
  recordCrossOriginIframes: false,
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scheduleFlush() {
  if (pendingIdleCallback !== null) return;

  if (typeof requestIdleCallback === 'function') {
    pendingIdleCallback = requestIdleCallback(() => {
      pendingIdleCallback = null;
      flush();
    }, { timeout: 5_000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    pendingIdleCallback = setTimeout(() => {
      pendingIdleCallback = null;
      flush();
    }, 100) as unknown as number;
  }
}

function flush() {
  if (eventBuffer.length === 0 || !sessionId) return;

  const eventsToSend = eventBuffer;
  eventBuffer = [];
  const seq = sequenceNumber++;

  sessionReplayApi
    .flushEvents({
      sessionId,
      events: JSON.stringify(eventsToSend),
      sequenceNumber: seq,
    })
    .then(() => {
      // Cache token on successful flush so stopRecording can use it after logout
      lastKnownToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    })
    .catch(() => {
      // If flush fails, discard — we do not re-queue to avoid unbounded growth.
      // The server can tolerate gaps; sessions with missing chunks are still useful.
    });
}

function bufferEvent(event: eventWithTime) {
  eventBuffer.push(event);
  if (eventBuffer.length >= BUFFER_FLUSH_THRESHOLD) {
    scheduleFlush();
  }
}

function getFinalRequestHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function flushBufferedEventsWithFetch(token: string | null) {
  if (eventBuffer.length === 0 || !sessionId) return;

  const seq = sequenceNumber++;
  const body = JSON.stringify({
    sessionId,
    events: JSON.stringify(eventBuffer),
    sequenceNumber: seq,
  });
  eventBuffer = [];

  fetch(`${import.meta.env.VITE_API_URL}/session-replay/events`, {
    method: 'POST',
    headers: getFinalRequestHeaders(token),
    body,
    keepalive: true,
  }).catch(() => { /* best-effort */ });
}

function sendEndSessionBeacon(): boolean {
  if (!sessionId || !endSessionToken || typeof navigator.sendBeacon !== 'function') {
    return false;
  }

  const body = new URLSearchParams({
    sessionId,
    endSessionToken,
  });

  return navigator.sendBeacon(`${import.meta.env.VITE_API_URL}/session-replay/end-beacon`, body);
}

function endSessionWithFetch(token: string | null) {
  if (!sessionId || closeoutSent) return;

  closeoutSent = true;
  fetch(`${import.meta.env.VITE_API_URL}/session-replay/end`, {
    method: 'POST',
    headers: getFinalRequestHeaders(token),
    body: JSON.stringify({
      sessionId,
      ...(endSessionToken ? { endSessionToken } : {}),
    }),
    keepalive: true,
  }).catch(() => { /* best-effort */ });
}

function closeCurrentSession(preferBeacon: boolean) {
  if (!sessionId || closeoutSent) return;

  if (preferBeacon && sendEndSessionBeacon()) {
    closeoutSent = true;
    return;
  }

  const token = lastKnownToken ?? localStorage.getItem(ACCESS_TOKEN_KEY);
  endSessionWithFetch(token);
}

// ---------------------------------------------------------------------------
// Idle detection
// ---------------------------------------------------------------------------

function resetIdleTimer() {
  if (idleTimeoutId !== null) {
    clearTimeout(idleTimeoutId);
  }
  idleTimeoutId = setTimeout(onIdleTimeout, IDLE_TIMEOUT_MS);
}

function onIdleTimeout() {
  isIdle = true;
  stopRecording();
  // Listen for activity to resume
  addIdleActivityListeners();
}

function onActivityAfterIdle() {
  if (!isIdle) return;
  isIdle = false;
  removeIdleActivityListeners();
  // Only resume if still authenticated
  if (useAuthStore.getState().isAuthenticated) {
    void startRecording();
  }
}

function throttledActivityHandler() {
  const now = Date.now();
  if (now - lastActivityTs < IDLE_THROTTLE_MS) return;
  lastActivityTs = now;
  if (isIdle) {
    onActivityAfterIdle();
  } else {
    resetIdleTimer();
  }
}

function addIdleActivityListeners() {
  handleIdleActivity = throttledActivityHandler;
  window.addEventListener('mousemove', handleIdleActivity, { passive: true });
  window.addEventListener('pointerdown', handleIdleActivity, { passive: true });
  window.addEventListener('keydown', handleIdleActivity, { passive: true });
  window.addEventListener('scroll', handleIdleActivity, { passive: true });
}

function removeIdleActivityListeners() {
  if (handleIdleActivity) {
    window.removeEventListener('mousemove', handleIdleActivity);
    window.removeEventListener('pointerdown', handleIdleActivity);
    window.removeEventListener('keydown', handleIdleActivity);
    window.removeEventListener('scroll', handleIdleActivity);
    handleIdleActivity = null;
  }
}

// ---------------------------------------------------------------------------
// Browser shutdown/navigation — use Beacon where possible to end the session.
// ---------------------------------------------------------------------------

function addBeforeUnloadHandler() {
  handleBeforeUnload = () => {
    const token = lastKnownToken ?? localStorage.getItem(ACCESS_TOKEN_KEY);
    flushBufferedEventsWithFetch(token);
    closeCurrentSession(true);
  };

  handlePageHide = () => {
    const token = lastKnownToken ?? localStorage.getItem(ACCESS_TOKEN_KEY);
    flushBufferedEventsWithFetch(token);
    closeCurrentSession(true);
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('pagehide', handlePageHide);
}

function removeBeforeUnloadHandler() {
  if (handleBeforeUnload) {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    handleBeforeUnload = null;
  }
  if (handlePageHide) {
    window.removeEventListener('pagehide', handlePageHide);
    handlePageHide = null;
  }
}

// ---------------------------------------------------------------------------
// Global error capture
// ---------------------------------------------------------------------------

let handleGlobalError: ((event: ErrorEvent) => void) | null = null;
let handleUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null = null;

function startErrorTracking() {
  handleGlobalError = (event: ErrorEvent) => {
    record.addCustomEvent('error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  };

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    record.addCustomEvent('error', {
      message: event.reason?.message ?? String(event.reason),
      type: 'unhandledrejection',
    });
  };

  window.addEventListener('error', handleGlobalError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}

function stopErrorTracking() {
  if (handleGlobalError) {
    window.removeEventListener('error', handleGlobalError);
    handleGlobalError = null;
  }
  if (handleUnhandledRejection) {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    handleUnhandledRejection = null;
  }
}

// ---------------------------------------------------------------------------
// SPA route change tracking
// ---------------------------------------------------------------------------

let lastTrackedPath = '';
let originalPushState: typeof history.pushState | null = null;
let originalReplaceState: typeof history.replaceState | null = null;
let handlePopState: (() => void) | null = null;

function emitRouteChange() {
  const path = window.location.pathname;
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;
  // Emit as a custom rrweb event (type 5) that the server can detect
  record.addCustomEvent('navigation', { href: window.location.href });
}

function startRouteTracking() {
  lastTrackedPath = window.location.pathname;

  // Patch pushState/replaceState to detect SPA navigations
  originalPushState = history.pushState.bind(history);
  originalReplaceState = history.replaceState.bind(history);

  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    originalPushState!(...args);
    emitRouteChange();
  };
  history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
    originalReplaceState!(...args);
    emitRouteChange();
  };

  // Also catch back/forward navigation
  handlePopState = emitRouteChange;
  window.addEventListener('popstate', handlePopState);
}

function stopRouteTracking() {
  if (originalPushState) {
    history.pushState = originalPushState;
    originalPushState = null;
  }
  if (originalReplaceState) {
    history.replaceState = originalReplaceState;
    originalReplaceState = null;
  }
  if (handlePopState) {
    window.removeEventListener('popstate', handlePopState);
    handlePopState = null;
  }
}

// ---------------------------------------------------------------------------
// Start / Stop recording
// ---------------------------------------------------------------------------

async function startRecording() {
  if (isRecording || isStarting || !useAuthStore.getState().isAuthenticated) return;

  isStarting = true;
  let newSessionId: string | null = null;
  let tokenAtStart: string | null = null;
  let createdEndSessionToken: string | null = null;
  try {
    if (import.meta.env.VITE_SESSION_REPLAY_ENABLED !== 'true' || isRecording || !useAuthStore.getState().isAuthenticated) return;

    newSessionId = crypto.randomUUID();
    tokenAtStart = localStorage.getItem(ACCESS_TOKEN_KEY);
    lastKnownToken = tokenAtStart;

    const { data } = await sessionReplayApi.createSession({
      sessionId: newSessionId,
      userAgent: navigator.userAgent,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      startUrl: window.location.pathname,
    });
    createdEndSessionToken = data.endSessionToken;
  } catch {
    return;
  } finally {
    isStarting = false;
  }

  if (!newSessionId) return;

  if (isRecording || !useAuthStore.getState().isAuthenticated) {
    if (createdEndSessionToken) {
      const headers = getFinalRequestHeaders(tokenAtStart);
      fetch(`${import.meta.env.VITE_API_URL}/session-replay/end`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sessionId: newSessionId,
          endSessionToken: createdEndSessionToken,
        }),
        keepalive: true,
      }).catch(() => { /* best-effort */ });
    }
    return;
  }

  sessionId = newSessionId;
  endSessionToken = createdEndSessionToken;
  window.__endoSessionId = sessionId;
  sequenceNumber = 0;
  eventBuffer = [];
  isRecording = true;
  closeoutSent = false;

  // Start rrweb recording with console error capture
  const stop = record({
    emit: bufferEvent,
    plugins: [
      getRecordConsolePlugin({
        level: ['error', 'warn'],
        lengthThreshold: 1000,
      }),
    ],
    ...RRWEB_OPTIONS,
  });
  stopRecordingFn = stop ?? null;

  // Track SPA route changes and JS errors as custom rrweb events
  startRouteTracking();
  startErrorTracking();

  // Periodic flush
  flushIntervalId = setInterval(flush, FLUSH_INTERVAL_MS);

  // Idle detection — listen during recording
  lastActivityTs = Date.now();
  resetIdleTimer();
  // Use the same throttled handler for idle detection while recording
  window.addEventListener('mousemove', throttledActivityHandler, { passive: true });
  window.addEventListener('pointerdown', throttledActivityHandler, { passive: true });
  window.addEventListener('keydown', throttledActivityHandler, { passive: true });
  window.addEventListener('scroll', throttledActivityHandler, { passive: true });

  // beforeunload
  addBeforeUnloadHandler();
}

function stopRecording() {
  if (!isRecording) return;
  isRecording = false;

  // Stop rrweb
  if (stopRecordingFn) {
    stopRecordingFn();
    stopRecordingFn = null;
  }

  // Cancel pending idle callback
  if (pendingIdleCallback !== null) {
    if (typeof cancelIdleCallback === 'function') {
      cancelIdleCallback(pendingIdleCallback);
    } else {
      clearTimeout(pendingIdleCallback);
    }
    pendingIdleCallback = null;
  }

  // Clear flush interval
  if (flushIntervalId !== null) {
    clearInterval(flushIntervalId);
    flushIntervalId = null;
  }

  // Clear idle timer
  if (idleTimeoutId !== null) {
    clearTimeout(idleTimeoutId);
    idleTimeoutId = null;
  }

  // Remove activity listeners used during recording
  window.removeEventListener('mousemove', throttledActivityHandler);
  window.removeEventListener('pointerdown', throttledActivityHandler);
  window.removeEventListener('keydown', throttledActivityHandler);
  window.removeEventListener('scroll', throttledActivityHandler);

  // Use cached token for final flush (token may already be cleared on logout)
  const token = lastKnownToken ?? localStorage.getItem(ACCESS_TOKEN_KEY);

  // Final flush via fetch (axios interceptor may not have a valid token post-logout)
  flushBufferedEventsWithFetch(token);

  // End session on server
  closeCurrentSession(false);

  // Remove beforeunload, route tracking, and error tracking
  removeBeforeUnloadHandler();
  stopRouteTracking();
  stopErrorTracking();

  sessionId = null;
  endSessionToken = null;
  window.__endoSessionId = undefined;
  lastKnownToken = null;
  closeoutSent = false;
}

export function getSessionId(): string | null {
  return sessionId;
}

// ---------------------------------------------------------------------------
// Initialization — called from main.tsx
// ---------------------------------------------------------------------------

export function initSessionReplay() {
  if (import.meta.env.VITE_SESSION_REPLAY_ENABLED !== 'true') return;

  // Subscribe to auth state changes
  useAuthStore.subscribe((state, prevState) => {
    if (state.isAuthenticated && !prevState.isAuthenticated) {
      void startRecording();
    } else if (!state.isAuthenticated && prevState.isAuthenticated) {
      stopRecording();
    }
  });

  // If the user is already authenticated when we initialize (e.g. page reload
  // with a valid token in localStorage), start recording immediately.
  if (useAuthStore.getState().isAuthenticated) {
    void startRecording();
  }
}
